import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
  SelectChangeEvent,
  Box,
} from '@mui/material';
import { Close as CloseIcon, PersonAdd as PersonAddIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface School {
  id: number;
  name: string;
}

interface Grade {
  id: number;
  name: string;
  school_id: number;
  teacher?: number | null;
  School: { name: string };
}

export interface UserFormValues {
  name: string;
  email: string;
  role_id: number;
  school_id: number;
  grade_ids: number[];
  password?: string;
}

export interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: UserFormValues) => Promise<void>;
  isEditMode: boolean;
  initialFormData: UserFormValues;
  schools: School[];
  grades: Grade[];
  isSaving: boolean;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
  initialFormData,
  schools,
  grades,
  isSaving,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Local state for the form
  const [formData, setFormData] = useState<UserFormValues>(initialFormData);

  // Update local state when initial data changes (for editing)
  useEffect(() => {
    setFormData(initialFormData);
  }, [initialFormData]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await onSubmit(formData);
  };

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSelectChange = useCallback((event: SelectChangeEvent<number>) => {
    const { name, value } = event.target;
    if (name === 'school_id') {
      setFormData(prev => ({
        ...prev,
        school_id: value as number,
        grade_ids: [], // Reset grades when school changes
      }));
    } else {
      setFormData(prev => ({ ...prev, [name as string]: value }));
    }
  }, []);

  const handleGradeChange = useCallback((value: number | string) => {
    setFormData(prev => ({ ...prev, grade_ids: value ? [value as number] : [] }));
  }, []);

  const getTitle = () => {
    return isEditMode ? t('editUser') : t('addNewUser');
  };

  const getIcon = () => {
    const IconComponent = isEditMode ? EditIcon : PersonAddIcon;
    return (
      <IconComponent
        sx={{
          backgroundColor: 'white',
          fontSize: 46,
          border: "4px solid #b689e3",
          borderRadius: '50%',
          padding: '4px',
          path: { color: '#b689e3' },
        }}
      />
    );
  };

  const filteredGrades = grades.filter((grade) => !formData.school_id || grade.school_id === formData.school_id);

  const isFormValid = formData.name.trim() !== '' && formData.email.trim() !== '';

  return (
    <Dialog
      open={isOpen}
      onClose={isSaving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={isSaving}
      className="modal user-form-modal"
      slotProps={{
        paper: {
          className: isMobile ? 'modal-paper mobile' : 'modal-paper',
        },
      }}
    >
      <IconButton onClick={onClose} disabled={isSaving} className="close-button">
        <CloseIcon />
      </IconButton>

      <DialogTitle className="modal-title">
        {getTitle()}
        {getIcon()}
      </DialogTitle>

      <DialogContent className="modal-content">
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <div className="form-container">
            <label>פרטי משתמש:</label>
            {/* שורה 1: שם ואימייל */}
            <div className="form-row">
              <TextField
                required
                fullWidth
                margin="normal"
                label={t('fullName')}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isSaving}
                className="form-field"
              />

              <TextField
                required
                type="email"
                fullWidth
                margin="normal"
                label={t('email')}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isSaving}
                className="form-field"
              />
            </div>

            {/* שורה 2: תפקיד ובית ספר */}
            <div className="form-row">
              <FormControl fullWidth margin="normal" disabled={isSaving} className="form-field">
                <InputLabel>{t('role')}</InputLabel>
                <Select name="role_id" value={formData.role_id} label={t('role')} onChange={handleSelectChange}>
                  <MenuItem value={1}>{t('roleTeacher')}</MenuItem>
                  <MenuItem value={2}>{t('roleStudent')}</MenuItem>
                  <MenuItem value={3}>{t('roleAdmin')}</MenuItem>
                  <MenuItem value={4}>{t('rolePrincipal')}</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal" disabled={isSaving} className="form-field">
                <InputLabel>{t('school')}</InputLabel>
                <Select
                  name="school_id"
                  value={formData.school_id || ''}
                  label={t('school')}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="">
                    <em>{t('noSchool')}</em>
                  </MenuItem>
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* שורה 3: כיתות (רק אם נבחר בית ספר) */}
            {!!formData.school_id && (
              <>
                {formData.role_id === 2 && ( // Student - כיתה יחידה
                  <FormControl fullWidth margin="normal" disabled={isSaving}>
                    <InputLabel>{t('assignedClass')}</InputLabel>
                    <Select
                      name="grade_ids"
                      value={formData.grade_ids[0] || ''}
                      label={t('assignedClass')}
                      onChange={(e) => handleGradeChange(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t('none')}</em>
                      </MenuItem>
                      {filteredGrades.map((grade) => (
                        <MenuItem key={grade.id} value={grade.id}>
                          {grade.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {formData.role_id === 1 && ( // Teacher - כיתה אופציונלית
                  <FormControl fullWidth margin="normal" disabled={isSaving}>
                    <InputLabel>{t('assignedClass')}</InputLabel>
                    <Select
                      name="grade_ids"
                      value={formData.grade_ids[0] || ''}
                      label={t('assignedClass')}
                      onChange={(e) => handleGradeChange(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t('none')}</em>
                      </MenuItem>
                      {filteredGrades.map((grade) => (
                        <MenuItem key={grade.id} value={grade.id}>
                          {grade.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </div>

          <DialogActions className="modal-actions" style={{ marginBottom: 0 }}>
            <Button type="button" onClick={onClose} variant="outlined" disabled={isSaving} className="cancel-button">
              {t('cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={isSaving || !isFormValid} className="submit-button">
              {isSaving ? (
                <>
                  <CircularProgress size={20} className="loading-spinner" />
                  {isEditMode ? t('saving') : t('adding')}
                </>
              ) : isEditMode ? (
                t('save')
              ) : (
                t('add')
              )}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>

      {/* Loading Overlay */}
      {isSaving && (
        <div className={isMobile ? 'loading-overlay mobile' : 'loading-overlay'}>
          <CircularProgress size={60} className="loading-spinner" />
          <div className="loading-text">{isEditMode ? t('updatingUser') : t('creatingUser')}</div>
        </div>
      )}
    </Dialog>
  );
};

export default UserFormModal;
